import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import _ from 'lodash';

import { useInitializeStore } from './initialize';

import CasinoGamesWidget from '../components/marketing/widgets/CasinoGamesWidget.vue';
import LiveCasinoGamesWidget from '../components/marketing/widgets/LiveCasinoGamesWidget.vue';
import PromotionsWidget from '../components/marketing/widgets/PromotionsWidget.vue';
import VirtualLiveWidget from '../components/marketing/widgets/VirtualLiveWidget.vue';

/**
 * Maps a widget type to the component that renders it. A new type is one entry.
 *
 * Kept outside store state: in state, Pinia would make every component a reactive
 * proxy, which Vue warns about and which costs work on every render.
 */
const COMPONENT_REGISTRY = {
	casino_games: markRaw(CasinoGamesWidget),
	live_casino_games: markRaw(LiveCasinoGamesWidget),
	promotions: markRaw(PromotionsWidget),
	virtual_live: markRaw(VirtualLiveWidget),
};

/**
 * Marketing sections are the configurable placements of the platform, resolved by
 * the backend into an ordered list of widgets for the active locale.
 */
export const useMarketingSectionsStore = defineStore('marketingSectionsStore', {
	state: () => ({
		sections: {},
		/** Guards against a second fetch while the first is still in flight. */
		loading: {},
	}),
	getters: {
		component_registry: () => COMPONENT_REGISTRY,

		/**
		 * The resolved section, or null while it has not been fetched.
		 */
		getSection: (state) => (slug) => state.sections[slug] || null,

		/**
		 * Widgets in their configured order, skipping types this build cannot render
		 * and any the caller excludes.
		 */
		getWidgets: (state) => (slug, exclude_types = []) => {
			const section = state.sections[slug];

			if (!section || !section.enabled) return [];

			return _.orderBy(section.widgets || [], 'sort', 'asc').filter(
				(widget) => COMPONENT_REGISTRY[widget.type] && !exclude_types.includes(widget.type)
			);
		},
	},
	actions: {
		/**
		 * Fetch a placement for the active locale and device.
		 *
		 * The locale travels on the global Api-Language header.
		 */
		fetchSection(slug) {
			if (this.loading[slug]) return Promise.resolve(this.sections[slug]);

			this.loading = { ...this.loading, [slug]: true };

			const device = useInitializeStore().isMobile ? 'mobile' : 'desktop';

			return global.axios.get(`${process.env.VUE_APP_API_URL}/marketing-sections/${slug}`, { params: { device } })
				.then((response) => {
					this.sections = { ...this.sections, [slug]: response.data.data };

					return Promise.resolve(response.data.data);
				})
				.catch((error) => {
					// A placement that cannot be resolved renders nothing rather than
					// breaking the page around it. It is marked failed so the next view
					// asks again instead of keeping the failure for the whole session.
					this.sections = { ...this.sections, [slug]: { slug, enabled: false, widgets: [], failed: true } };

					return Promise.reject(error);
				})
				.finally(() => {
					this.loading = { ...this.loading, [slug]: false };
				});
		},

		/**
		 * Whether a placement contains a given widget type. Used by placements that
		 * render a type outside the sidebar, such as Virtual Live on mobile.
		 */
		hasWidgetType(slug, type) {
			const section = this.sections[slug];

			if (!section || !section.enabled) return false;

			return (section.widgets || []).some((widget) => widget.type === type);
		},
	},
});
